import {
  buildScoutInternalEventMessage,
  parseRespondedScoutInternalEventsFromMessages,
  type ChatMessageLike,
  type ScoutInternalEventStatus,
} from "./scoutInternalEvents";

export {
  SCOUT_INTERNAL_EVENT_PREFIX,
  isSyntheticInternalEventMessage,
  stripScoutInternalEventLineFromText,
} from "./scoutInternalEvents";

export type TenantDocumentProposalEventStatus = ScoutInternalEventStatus;

function pluralizeDocument(count: number): string {
  return count === 1 ? "document" : "documents";
}

/**
 * Build the synthetic user message text for a tenant document-library
 * proposal action. Scout sees this in its context and uses the
 * confirmed/declined signal to decide what to do next; the chat UI hides it.
 */
export function buildTenantDocumentProposalEventMessage(input: {
  status: TenantDocumentProposalEventStatus;
  proposalId: string;
  /** Documents the user approved (confirm path) or the full set declined. */
  count: number;
  /** Original total proposed; used to note "of M proposed" when partial. */
  totalProposedCount: number;
}): string {
  const fileWord = pluralizeDocument(input.count);
  if (input.status === "confirmed") {
    const proposedNote =
      input.count === input.totalProposedCount ? "" : ` (of ${input.totalProposedCount} proposed)`;
    return buildScoutInternalEventMessage({
      kind: "document-library-proposal",
      status: "confirmed",
      proposalId: input.proposalId,
      detail:
        `${input.count} ${fileWord}${proposedNote}. ` +
        `Continue using the newly attached documents to answer the user's previous question.`,
    });
  }
  return buildScoutInternalEventMessage({
    kind: "document-library-proposal",
    status: "declined",
    proposalId: input.proposalId,
    detail:
      `${input.totalProposedCount} ${fileWord}. ` +
      `Proceed with the alternative you offered. Do not re-propose the same set.`,
  });
}

/**
 * Scan the persisted conversation history for tenant document-library
 * proposal events and return a map of `proposalId -> status`.
 */
export function parseRespondedTenantDocumentProposalsFromMessages(
  messages: ReadonlyArray<ChatMessageLike>
): Record<string, TenantDocumentProposalEventStatus> {
  return parseRespondedScoutInternalEventsFromMessages(messages, "document-library-proposal");
}
